import React from 'react';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DataService } from 'src/services/data-service';
import { useNavigate } from 'react-router-dom';
import GenericDialog from 'src/components/GenericDialog';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface IAddUsersProps {
  onSuccess: () => void;
  onClose: () => void;
}

const items = { admin: 'Admin', employee: 'Employee' };

const AddUser: React.FC<IAddUsersProps> = ({ onSuccess, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const formConfig = React.useMemo((): Record<string, DynamicFieldTypes> => ({
    name: {
      type: DynamicField.TEXT,
      required: true,
      name: 'name',
      title: 'Name',
      value: '',
      disabled: false
    },
    email: {
      type: DynamicField.TEXT,
      inputType: 'email',
      required: true,
      name: 'email',
      title: 'Email',
      value: '',
      disabled: false
    },
    password: {
      type: DynamicField.TEXT,
      inputType: 'password',
      required: true,
      name: 'password',
      title: 'Password',
      value: '',
      disabled: false,
      minChars: 6,
      placeholder: 'Minimum 6 characters',
      helperText: 'Password must be at least 6 characters long',
      customValidation: (value: any) => {
        if (!value || value.length < 6) {
          return 'Password must be at least 6 characters';
        }
        if (!/(?=.*[A-Z])/.test(value)) {
          return 'Password must contain at least one uppercase letter';
        }
        if (!/(?=.*[a-z])/.test(value)) {
          return 'Password must contain at least one lowercase letter';
        }
        if (!/(?=.*\d)/.test(value)) {
          return 'Password must contain at least one number';
        }
        return null;
      }
    },
    confirmPassword: {
      type: DynamicField.TEXT,
      inputType: 'password',
      required: true,
      name: 'confirmPassword',
      title: 'Confirm Password',
      value: '',
      disabled: false,
      placeholder: 'Re-enter password',
      customValidation: (value: any, allValues: Record<string, any>) => {
        if (value !== allValues.password) {
          return 'Passwords do not match';
        }
        return null;
      }
    },
    role: {
      type: DynamicField.SELECT,
      required: true,
      name: 'role',
      title: 'Role',
      value: '',
      disabled: false,
      items: items
    }
  }), []);

  const submit = React.useCallback(async (data: Record<string, any>) => {
    // Remove confirmPassword from the payload (it's only for validation)
    const { confirmPassword, ...payload } = data;

    const response = await DataService.post(
      'api/User/create',
      payload,
      undefined
    );

    if (response.ok) {
      onSuccess();
      return true;
    } else {
      const result: { message: string } = await response.json();
      dispatch(showAlert({ message: result.message || 'An error occurred!', type: 'error' }));
      return false;
    }
  }, [dispatch, onSuccess]);

  return (
    <GenericDialog
      open={true}
      title="Add User"
      onClose={onClose}
    >
      <DynamicFormWidget
        title=""
        fields={formConfig}
        onSubmit={submit}
      />
    </GenericDialog>
  );
};

export default AddUser;
